import { CommonModule } from '@angular/common';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, computed, DestroyRef, inject, Input, InputSignal, output, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DEFAULT_TOASTER_LIFE, ZendraLocalizationServices, ZnConfirmation, ZnMultiSelect, ZnSelectQueryParams } from '@zendra/shared';
import { map, Observable, of, switchMap } from 'rxjs';

import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { ContinuousInventoryRoutineModalWorkflow } from '../continuous-inventory-routine-modal-workflow';

interface ContinuousInventoryExecutionResult {
  message?: string | null;
}

interface ContinuousInventoryExecutionService {
  executeContinuousInventory(request: { meditionNumbers: number[] }): Observable<ContinuousInventoryExecutionResult>;
}

interface MeasurementCatalogService {
  loadMeditionNumbers(params?: ZnSelectQueryParams): Observable<{ items: { key: string; value: string }[]; totalCount: number }>;
  loadAllMeditionNumbers(search?: string): Observable<{ key: string; value: string }[]>;
}

@Component({
  selector: 'lib-action-execute-continuous-inventory',
  imports: [CommonModule, LocalizationPipe, ReactiveFormsModule, ZnMultiSelect],
  templateUrl: './action-execute-continuous-inventory.html',
})
export class ActionExecuteContinuousInventory implements IModalContent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);

  @ViewChild('measurementSelect') measurementSelect?: ZnMultiSelect;

  loading = signal(false);
  private readonly modalWorkflow = new ContinuousInventoryRoutineModalWorkflow({
    activeModal: this.activeModal,
    confirmationService: this.confirmationService,
    destroyRef: this.destroyRef,
    loading: this.loading,
    toaster: this.toaster,
  });
  completed = output<void>();
  selectedRecords?: InputSignal<any[]>;
  selectedRecordIds?: InputSignal<number[]>;

  @Input({ required: true }) executeService!: ContinuousInventoryExecutionService;
  @Input({ required: true }) catalogService!: MeasurementCatalogService;
  @Input({ required: true }) measurementLabelKey!: string;
  @Input() successToastLife = DEFAULT_TOASTER_LIFE;

  readonly localizationResource = ZendraLocalizationServices.Inventory;
  readonly selectedMeditionNumbers = signal<number[]>([]);
  readonly selectAllActive = signal(false);

  form: FormGroup = this.formBuilder.group({
    MeditionNumbers: [[] as string[]],
  });

  canSave = computed(() => this.selectedMeditionNumbers().length > 0 || this.selectAllActive());
  readonly meditionNumbers = (params?: ZnSelectQueryParams) => this.catalogService.loadMeditionNumbers(params);

  onMeasurementSelectionChange(value: string | string[] | null): void {
    let values: string[] = [];
    if (Array.isArray(value)) {
      values = value;
    } else if (value) {
      values = [value];
    }
    const selected = values.map(Number).filter(Number.isFinite);
    const selectAll = !!this.measurementSelect?.allSelected() && this.measurementSelect.displayOptions().length > 0;

    this.selectedMeditionNumbers.set(selected);
    this.selectAllActive.set(selectAll);
    this.form.markAsDirty();
  }

  confirm(): void {
    if (!this.canSave() || this.loading()) {
      return;
    }

    this.loading.set(true);
    this.modalWorkflow.submit(
      this.resolveMeditionNumbers().pipe(
        switchMap(meditionNumbers => this.executeService.executeContinuousInventory({ meditionNumbers })),
      ),
      () => this.successToastLife,
    );
  }

  dismiss(): void {
    this.modalWorkflow.dismiss(this.form.dirty, () => {
      this.form.reset({ MeditionNumbers: [] });
      this.selectedMeditionNumbers.set([]);
      this.selectAllActive.set(false);
    });
  }

  private resolveMeditionNumbers(): Observable<number[]> {
    if (!this.selectAllActive()) {
      return of(this.selectedMeditionNumbers());
    }

    return this.catalogService.loadAllMeditionNumbers(this.measurementSelect?.allSelectedSearchTerm() ?? undefined).pipe(
      map(options => options.map(option => Number(option.key)).filter(Number.isFinite)),
    );
  }
}
